import * as Yup from 'yup';

export interface OrgFormValues {
  name: string;
  shortName: string;
  internalCode: string;
  description: string;
  address: string;
  contactPerson: string;
  phone: string;
  email: string;
  status: 'active' | 'inactive';
  logo: File | null;
  /** Public URL returned by the upload endpoint once the logo is stored. */
  logoUrl: string;
}

export const initialOrgValues: OrgFormValues = {
  name: '',
  shortName: '',
  internalCode: '',
  description: '',
  address: '',
  contactPerson: '',
  phone: '',
  email: '',
  status: 'active',
  logo: null,
  logoUrl: '',
};

export const orgValidationSchema = Yup.object({
  // Only Organization Name + Short Name are required (per the step-1 design); the rest are
  // optional but still format-checked when provided.
  name: Yup.string().trim().required('Organization name is required'),
  shortName: Yup.string().trim().required('Short name is required'),
  description: Yup.string().trim(),
  address: Yup.string().trim(),
  contactPerson: Yup.string().trim(),
  phone: Yup.string()
    .trim()
    .matches(/^[6-9]\d{9}$/, 'Enter a valid 10-digit mobile number')
    .nullable(),
  email: Yup.string().trim().email('Enter a valid email address'),
  status: Yup.mixed<'active' | 'inactive'>().oneOf(['active', 'inactive']).required(),
});

/** Request body for POST /organisation/organization/. */
export interface OrgPayload {
  name: string;
  short_name: string;
  description: string;
  status: 'Active' | 'Inactive';
  address: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  logo_url: string;
  /** Platform state this client belongs to (e.g. "RJ"); scopes "Clients in <state>". */
  state_code?: string;
}

/** Prefill the step-1 form from an existing organization (edit mode). */
export const fromOrgResponse = (o: {
  name?: string | null;
  short_name?: string | null;
  internal_code?: string | null;
  description?: string | null;
  address?: string | null;
  contact_person?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  status?: string | null;
  logo_url?: string | null;
}): OrgFormValues => ({
  name: o.name ?? '',
  shortName: o.short_name ?? '',
  internalCode: o.internal_code ?? '',
  description: o.description ?? '',
  address: o.address ?? '',
  contactPerson: o.contact_person ?? '',
  // Stored national number only — strip the +91 (or any non-digits) and keep the last 10.
  phone: (o.contact_phone ?? '').replace(/\D/g, '').slice(-10),
  email: o.contact_email ?? '',
  status: (o.status ?? '').toLowerCase() === 'inactive' ? 'inactive' : 'active',
  logo: null,
  logoUrl: o.logo_url ?? '',
});

/** Map the step-1 form values onto the API payload shape. */
export const toOrgPayload = (v: OrgFormValues): OrgPayload => ({
  name: v.name.trim(),
  short_name: v.shortName.trim(),
  description: v.description.trim(),
  status: v.status === 'active' ? 'Active' : 'Inactive',
  address: v.address.trim(),
  contact_person: v.contactPerson.trim(),
  contact_phone: `+91${v.phone.trim()}`,
  contact_email: v.email.trim(),
  logo_url: v.logoUrl,
});
